import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck, Award, Handshake, Tag, Bike, Search } from "lucide-react";
import { fetchCars, fetchContent } from "../api";
import CarCard from "../components/CarCard";

const VALUE_ICONS = [Award, ShieldCheck, Handshake];

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [sold, setSold] = useState([]);
  const [c, setC] = useState(null);

  useEffect(() => {
    fetchContent("home").then(setC).catch(() => setC(null));
    fetchCars({ status: "available" })
      .then((cars) => {
        const feat = cars.filter((x) => x.featured);
        setFeatured((feat.length ? feat : cars).slice(0, 6));
      })
      .catch(() => setFeatured([]));
    fetchCars({ status: "sold" })
      .then((cars) => setSold(cars.slice(0, 8)))
      .catch(() => setSold([]));
  }, []);

  // Hero slides: featured cars (if enabled + available) else custom images from content
  const useFeatured = c ? c.useFeaturedInHero !== false : true;
  const customImages = (c && c.heroImages) || [];
  const heroSlides = useFeatured && featured.length
    ? featured.slice(0, 5).map((car) => ({ image: car.images[0], car }))
    : (customImages.length ? customImages : [""]).map((image) => ({ image, car: null }));

  useEffect(() => { setSlide(0); }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5500);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  if (!c) return <div className="py-40 text-center text-gray-500">Loading…</div>;

  const current = heroSlides[slide] || heroSlides[0];

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[68vh] md:h-[82vh] overflow-hidden gpm-bg-green">
        {heroSlides.map((s, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === slide ? 1 : 0 }}>
            {s.image && <img src={s.image} alt="GPM" className="w-full h-full object-cover" />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,41,29,0.82) 0%, rgba(20,41,29,0.35) 55%, rgba(20,41,29,0.15) 100%)" }} />
          </div>
        ))}

        <div className="relative max-w-7xl mx-auto px-6 h-full flex items-center">
          <div className="max-w-xl gpm-fade-up" key={slide}>
            <p className="gpm-text-gold uppercase tracking-[0.35em] text-xs md:text-sm mb-4">{c.heroEyebrow}</p>
            <h1 className="font-serif-display text-5xl md:text-7xl font-bold gpm-text-cream leading-none">
              {c.heroHeadingLine1}<br />{c.heroHeadingLine2}
            </h1>
            {current?.car ? (
              <div className="mt-6">
                <p className="gpm-text-cream opacity-85 text-base md:text-lg">Now featuring</p>
                <div className="flex flex-wrap items-baseline gap-3 mt-1">
                  <span className="font-serif-display text-2xl md:text-3xl gpm-text-cream font-semibold">{current.car.title}</span>
                  <span className="gpm-text-gold font-serif-display text-2xl font-bold">{current.car.priceLabel}</span>
                </div>
                <Link to={`/product/${current.car.slug}`} className="inline-flex items-center gap-2 mt-6 px-8 py-4 text-sm font-semibold uppercase tracking-wider gpm-text-green transition-transform hover:-translate-y-0.5" style={{ backgroundColor: "var(--gpm-cream)" }}>
                  View this car <ChevronRight size={18} />
                </Link>
              </div>
            ) : (
              <>
                <p className="gpm-text-cream opacity-85 mt-6 text-base md:text-lg">{c.heroSubtitle}</p>
                <Link to="/cars-for-sale" className="inline-flex items-center gap-2 mt-8 px-8 py-4 text-sm font-semibold uppercase tracking-wider gpm-text-green transition-transform hover:-translate-y-0.5" style={{ backgroundColor: "var(--gpm-cream)" }}>
                  {c.heroButtonLabel} <ChevronRight size={18} />
                </Link>
              </>
            )}
          </div>
        </div>

        {heroSlides.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} className="h-1.5 rounded-full transition-all" style={{ width: i === slide ? 32 : 12, background: i === slide ? "var(--gpm-gold)" : "rgba(232,226,204,0.5)" }} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        )}
      </section>

      {/* INTRO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="gpm-text-gold uppercase tracking-[0.3em] text-xs mb-4">{c.introEyebrow}</p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green mb-6">{c.introTitle}</h2>
          <div className="gpm-divider mx-auto mb-8" />
          <div className="text-gray-600 leading-relaxed text-lg space-y-5">
            {(c.introParagraphs || []).map((p, i) => <p key={i}>{p}</p>)}
            {c.introTagline && <p className="font-semibold gpm-text-green">{c.introTagline}</p>}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="py-16" style={{ backgroundColor: "#f4f2ea" }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="gpm-text-gold uppercase tracking-[0.3em] text-xs mb-2">{c.featuredEyebrow}</p>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green">{c.featuredTitle}</h2>
              </div>
              <Link to="/cars-for-sale" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide gpm-text-green hover:gpm-text-gold transition-colors">
                View all <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
              {featured.map((car) => <CarCard key={car.id} car={car} />)}
            </div>
          </div>
        </section>
      )}

      {/* OUR SERVICES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="gpm-text-gold uppercase tracking-[0.3em] text-xs mb-3">{c.servicesEyebrow}</p>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green">{c.servicesTitle}</h2>
            <div className="gpm-divider mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Tag, title: "Car Selling", text: "We sell your car on your behalf — marketing, enquiries and negotiation handled.", to: "/services" },
              { icon: Bike, title: "Motorcycles", text: "Buy, sell and source classic and modern-classic motorcycles.", to: "/motorcycles" },
              { icon: Search, title: "Car Sourcing", text: "Tell us what you want and our network tracks down the perfect example.", to: "/car-sourcing" },
              { icon: Award, title: "Classic Car Sourcing", text: "Specialist sourcing for rare, concours and collectible motorcars.", to: "/car-sourcing" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group border border-black/10 p-6 hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 gpm-bg-green">
                    <Icon size={24} className="gpm-text-gold" />
                  </div>
                  <h3 className="font-serif-display text-xl font-semibold gpm-text-green mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{s.text}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide gpm-text-green group-hover:gpm-text-gold transition-colors">
                    Learn more <ChevronRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 gpm-bg-green">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="gpm-text-gold uppercase tracking-[0.3em] text-xs mb-3">{c.valuesEyebrow}</p>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-cream">{c.valuesTitle}</h2>
            <div className="gpm-divider mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {(c.values || []).map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <div key={i} className="text-center px-4">
                  <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 border-2 gpm-border-gold">
                    <Icon size={26} className="gpm-text-gold" />
                  </div>
                  <h3 className="font-serif-display text-xl gpm-text-cream mb-3">{v.title}</h3>
                  <p className="gpm-text-cream opacity-75 text-sm leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLD ARCHIVE STRIP */}
      {sold.length > 0 && (
        <section className="py-16 bg-white border-t border-black/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="gpm-text-gold uppercase tracking-[0.3em] text-xs mb-2">Trusted by Enthusiasts</p>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green">Recently Sold</h2>
              </div>
              <Link to="/sold" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide gpm-text-green hover:gpm-text-gold transition-colors">
                View archive <ChevronRight size={16} />
              </Link>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4 -mx-1 px-1">
              {sold.map((car) => (
                <div key={car.id} className="group relative shrink-0 w-64 overflow-hidden border border-black/10">
                  <div className="relative aspect-[3/2] overflow-hidden">
                    <img src={car.images[0]} alt={car.title} className="gpm-card-img w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500" />
                    <span className="absolute top-3 left-3 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white" style={{ background: "rgba(122,32,32,0.85)" }}>Sold</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif-display text-lg font-semibold gpm-text-green leading-tight">{car.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{car.year}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: "#f4f2ea" }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold gpm-text-green mb-5">{c.ctaTitle}</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">{c.ctaText}</p>
          <Link to="/car-sourcing" className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-wider gpm-text-cream transition-transform hover:-translate-y-0.5" style={{ backgroundColor: "var(--gpm-green)" }}>
            {c.ctaButtonLabel} <ChevronRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
